/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable @next/next/no-img-element */
/* eslint-disable i18next/no-literal-string */
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { useCreateInvoice } from '../../hooks/useCreateInvoice'
import { EventHookInterface } from '../../types'
import { mobileScreen } from '../../utils'
import { core } from '../../utils/core'
import { CTA } from '../CTA'
import { H2, H4 } from '../Headings'
import { Hyperlink } from '../Hyperlink'
import { MailIcon } from '../icons'

const IFrame = styled.iframe`
  width: 100%;
  height: 80vh;
  border: 0;
`

const ErrorContainer = styled.div`
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90%;
  justify-content: center;
  & .buttonGroup {
    margin-top: 40px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 25px;

    & .lastContent {
      margin-top: 10px;
    }
  }
  & .description {
    text-align: center;
  }
`

const InfoContainer = styled.div`
  padding: 32px;
  max-height: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    font-weight: 400;
    font-size: 26px;
    line-height: 125%;
    text-align: center;
    margin-bottom: 0;
  }

  .info {
    &__desk {
      font-weight: 400;
      font-size: 16px;
      line-height: 140%;
      text-align: center;
      padding: 0 68px;
      ${mobileScreen(css`
        padding: 0;
      `)}
    }

    &__button {
      width: 100%;
    }

    &__panel {
      display: flex;
      position: relative;
      cursor: pointer;
      padding: 14px 0;

      p {
        font-weight: 500;
        font-size: 15px;
        line-height: 140%;
        margin: 0;
        padding-left: 14px;
        width: 100%;
      }
    }

    &__subPanel {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-out;
      margin: 0;
      padding: 0;

      li {
        list-style-type: none;
        padding: 10px 20px;
        display: flex;
        justify-content: space-between;
        font-weight: 500;
      }

      img {
        padding: 0 15px;
        min-width: 69px;
      }
    }

    &__arrow {
      position: absolute;
      right: 0;
    }

    &__block {
      display: flex;
      align-items: center;
      gap: 22px;
      ${mobileScreen(css`
        gap: 0;
      `)}
    }

    &__text {
      font-weight: 500;
      font-size: 11px;
      line-height: 140%;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      text-align: right;
    }
  }

  .refundable {
    color: #27ae60;
  }
`

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin: 30px auto;
  width: 264px;

  ${mobileScreen(css`
    flex-direction: row;
  `)}
`

const BankInfo = styled.div`
  overflow-y: scroll;
  padding-right: 16px;

  ::-webkit-scrollbar {
    width: 8px;
    background-color: rgba(157, 150, 157, 0.05);
    border-radius: 13px;
  }

  ::-webkit-scrollbar-thumb {
    background: #e5e6eb;
    border-radius: 20px;
  }
`

const Status: React.FC<{ status: 'refundable' | 'non-refundable' }> = ({
  status,
}) => {
  return status === 'refundable' ? (
    <p className="info__text refundable">refundable</p>
  ) : (
    <p className="info__text">non-refundable</p>
  )
}

export const XenditInvoiceForm: React.FC<{
  onClose: (success?: boolean) => void
  eventHook: EventHookInterface
}> = ({ eventHook, onClose }) => {
  const [isInvoiceCreating, setIsInvoiceCreating] = useState(false)
  const [isRequestMade, setIsRequestMade] = useState(false)
  const [showPaymentInfo, setShowPaymentInfo] = useState(true)
  const [isUserConfirmPayment, setIsUserConfirmPayment] = useState(false)
  const { value } = eventHook
  const createInvoice = useCreateInvoice({
    eventId: value.id,
    groupId: value.hosted_group.id,
  })
  const refContainer = useRef<HTMLIFrameElement>()
  const [isError, setIsError] = useState<boolean>(false)
  const router = useRouter()
  const { t } = useTranslation()

  useEffect(() => {
    if (isRequestMade === false && isUserConfirmPayment === true) {
      setIsRequestMade(true)
      createInvoice().then(
        (invoice: any) => {
          const invoiceUrl = invoice.data.invoice_url
          setIsInvoiceCreating(false)
          if (refContainer.current) {
            refContainer.current.src = invoiceUrl
          }
        },
        () => {
          setIsInvoiceCreating(false)
          setIsError(true)
        },
      )
    }
  }, [createInvoice, isInvoiceCreating, isRequestMade, showPaymentInfo])

  const handleSuccess = () => {
    setIsUserConfirmPayment(true)
    setShowPaymentInfo(false)
    setIsInvoiceCreating(true)
  }

  const hancleClick = (e: any) => {
    const { parentElement } = e.target
    const panel =
      parentElement.nextElementSibling.tagName === 'UL'
        ? parentElement.nextElementSibling
        : e.target.nextElementSibling

    if (panel.style.maxHeight) {
      panel.style.maxHeight = null
    } else {
      panel.style.maxHeight = panel.scrollHeight + 'px'
    }
  }

  if (isInvoiceCreating) {
    return (
      <object
        className="spinnerContainer"
        type="image/svg+xml"
        data="/animations/loadingSpinner.svg"></object>
    )
  }

  if (isError) {
    return (
      <ErrorContainer>
        <H2>{t(`Something went wrong`, `Something went wrong`)}</H2>
        <H4 className="description">
          {t('An error has occurred.', 'An error has occurred.')}
          <br />
          {t(
            'Please contact the site administrator.',
            'Please contact the site administrator.',
          )}
        </H4>
        <div className="buttonGroup">
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <CTA
            className="button outline-purple extra-padding-20"
            onClick={() => {
              router.push('/myevents')
            }}>
            {t(`Return to ‘My Events’`, `Return to ‘My Events’`)}
          </CTA>
          <Hyperlink href={`mailto:${core.contactEmail}`}>
            <CTA
              className="button purple with-icon centered extra-padding-20"
              onClick={() => onClose(false)}>
              <MailIcon />
              <span>{t(`Contact us`, `Contact us`)}</span>
            </CTA>
          </Hyperlink>
        </div>
      </ErrorContainer>
    )
  }

  return showPaymentInfo ? (
    <InfoContainer>
      <h2>Purchase a ticket</h2>
      <p className="info__desk">
        Before you complete your payment, it is important to note that{' '}
        <strong>not all methods of payment are eligible for a refund</strong>.
        Additionally, the transaction fee is non-refundable. See your options
        below.
      </p>

      <BankInfo>
        <div className="info__panel" onClick={hancleClick}>
          <img src="/icons/icon-bank.svg" />

          <p>Bank Transfer</p>

          <img className="info__arrow" src="/icons/icon-arrow-down-black.svg" />
        </div>

        <ul className="info__subPanel">
          <li>
            <div className="info__block">
              <img src="/icons/mandiri.png" height={20} />

              <p>Mandiri</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/bri.png" height={20} />

              <p>BRI</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/bni.png" height={20} />

              <p>BNI</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/permata.png" height={20} />

              <p>Permata</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/bca.png" height={20} />

              <p>BCA</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/icon-bank.svg" height={20} />

              <p>Other Banks</p>
            </div>

            <Status status="non-refundable" />
          </li>
        </ul>

        <div className="info__panel" onClick={hancleClick}>
          <img src="/icons/icon-card-black.svg" />

          <p>Credit / Debit Card</p>

          <img className="info__arrow" src="/icons/icon-arrow-down-black.svg" />
        </div>

        <ul className="info__subPanel">
          <li>
            <div className="info__block">
              <img src="/icons/icon-card-black.svg" height={20} />

              <p>Credit Card</p>
            </div>

            <Status status="refundable" />
          </li>
        </ul>

        <div className="info__panel" onClick={hancleClick}>
          <img src="/icons/icon-retail.svg" />

          <p>Retail Outlets</p>

          <img className="info__arrow" src="/icons/icon-arrow-down-black.svg" />
        </div>

        <ul className="info__subPanel">
          <li>
            <div className="info__block">
              <img src="/icons/alfamart.png" height={20} />

              <p>Alfamart</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/indomaret.png" height={20} />

              <p>Indomaret</p>
            </div>

            <Status status="non-refundable" />
          </li>
        </ul>

        <div className="info__panel" onClick={hancleClick}>
          <img src="/icons/icon-ewallet.svg" />

          <p>E-wallet / QRIS</p>

          <img className="info__arrow" src="/icons/icon-arrow-down-black.svg" />
        </div>

        <ul className="info__subPanel">
          <li>
            <div className="info__block">
              <img src="/icons/ovo.png" height={20} />

              <p>Ovo</p>
            </div>

            <Status status="non-refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/shopee-pay.png" height={20} />

              <p>Shopee Pay</p>
            </div>

            <Status status="refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/dana.png" height={20} />

              <p>Dana</p>
            </div>

            <Status status="refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/linkaja.png" height={20} />

              <p>Linkaja</p>
            </div>

            <Status status="refundable" />
          </li>

          <li>
            <div className="info__block">
              <img src="/icons/astropay.png" height={20} />

              <p>AstroPay</p>
            </div>

            <Status status="non-refundable" />
          </li>
        </ul>

        <div className="info__panel" onClick={hancleClick}>
          <img src="/icons/icon-debit.svg" />

          <p>Direct Debit</p>

          <img className="info__arrow" src="/icons/icon-arrow-down-black.svg" />
        </div>

        <ul className="info__subPanel">
          <li>
            <div className="info__block">
              <img src="/icons/bri.png" height={20} />

              <p>BRI</p>
            </div>

            <Status status="refundable" />
          </li>
        </ul>
      </BankInfo>

      <Buttons>
        <CTA className="green block info__button" onClick={handleSuccess}>
          {t(`I understand`, `I understand`)}
        </CTA>
        <CTA
          className="cancelButton info__button"
          onClick={() => onClose(false)}>
          {t(`Go back`, `Go back`)}
        </CTA>
      </Buttons>
    </InfoContainer>
  ) : (
    <IFrame ref={refContainer}></IFrame>
  )
}
